import './cards.css';
import { clsx } from 'clsx';
import { useEffect, useId, useState } from 'react';
import { TIER_SHADOW } from '#shared/core/ratings';
import type { PortraitArtRef, StudioPreset } from '#shared/schemas/media';
import type { PlayerDetail, PlayerListItem } from '#shared/schemas/player';
import type { RatingCard } from '#shared/schemas/rating';
import { tickerValueAt, useReducedMotion } from '@/ui';
import { useFigureArt } from './art';
import { CardBackSvg } from './CardBack';
import { CardSvg } from './CardSvg';
import { copy } from './copy';
import {
  cardAriaLabel,
  cardOvr,
  cardState,
  cardTier,
  jerseyText,
  largeAttrs,
  plateName,
  subline,
  tierLabel,
  topAttrs,
} from './model';
import { COUNT_MS, REVEAL_ORDER, type RevealStateName, revealFlags } from './reveal';

/**
 * The pack-reveal states (ux.md "Pack reveal"), in order. `settled` is the resting card. The cards
 * module owns the machine that walks them (`useRevealSequence`, `CardReveal`); every other module
 * only passes a state in (Draft Night passes one, everything else leaves it out).
 */
export const REVEAL_STATES = REVEAL_ORDER;
export type RevealState = RevealStateName;

/**
 * FROZEN props (docs/CONTRACTS.md "Cards API"). Callers pass the player and, separately, the
 * rating card from stats (`useRatingCard(s)`), so a roster screen can show names before ratings
 * arrive. `rating: null` draws the unrated "??" card.
 */
export interface PlayerCardProps {
  player: PlayerListItem | PlayerDetail;
  rating: RatingCard | null;
  size: 'l' | 'm' | 's';
  revealState?: RevealState;
  onPress?: () => void;
  /** Draw this art instead of `player.portrait` (studio previews a portrait that is not active yet). */
  portrait?: PortraitArtRef | null;
  /** Force a backdrop (studio's preset picker draws one mock card per preset). */
  preset?: StudioPreset;
  /** Extra classes on the root, for width overrides. Added after the card builder, backward compatible. */
  className?: string;
}

/** Root width per size. The card keeps its 5:7 shape from the SVG viewBox. */
const WIDTH: Record<PlayerCardProps['size'], string> = {
  l: 'w-full max-w-[343px]',
  m: 'w-40',
  s: 'w-[72px]',
};

/** CSS-var shadow on screen: grape on lilac, ink in Sun mode. */
const SCREEN_SHADOW = `var(--color-shade, ${TIER_SHADOW})`;

function useUid(): string {
  return useId().replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * The OVR as the card prints it. Outside the `count` state it is the real number. In `count` it
 * ticks from 0 to the target over COUNT_MS with the same curve as NumberTicker.
 */
export function useShownOvr(target: number | null, counting: boolean): number | null {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState<number>(0);
  useEffect(() => {
    if (!counting || target === null || reduced) return;
    let raf = 0;
    let t0: number | null = null;
    setShown(0);
    const frame = (ts: number) => {
      if (t0 === null) t0 = ts;
      const elapsed = ts - t0;
      setShown(tickerValueAt(0, target, elapsed, COUNT_MS));
      if (elapsed < COUNT_MS) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [counting, target, reduced]);
  if (target === null) return null;
  return counting && !reduced ? shown : target;
}

/**
 * The player card: one SVG on the CARD_GEOMETRY board, three sizes (L six attributes, M top three,
 * S the OVR chip). The figure slot takes a sticker (or bakes one from a cutout, or falls back to
 * the art in the window, or to the built-in mannequin). `revealState` draws one step of the pack
 * reveal; leave it out for the resting card.
 */
export function PlayerCard({
  player,
  rating,
  size,
  revealState,
  onPress,
  portrait,
  preset,
  className,
}: PlayerCardProps) {
  const uid = useUid();
  const art = portrait !== undefined ? portrait : player.portrait;
  const figure = useFigureArt(art, size);
  const ovr = cardOvr(rating);
  const tier = cardTier(rating);
  const state = cardState(rating);
  const flags = revealFlags(revealState);
  const shownOvr = useShownOvr(ovr, flags.ovrNumber === 'count');
  const provisional = state === 'provisional';
  const rusty = state === 'rusty';
  const attrs = size === 'l' ? largeAttrs(rating) : size === 'm' ? topAttrs(rating, 3) : [];
  const showAttrs = size === 'm' && state === 'unrated' ? [] : attrs;
  const title = cardAriaLabel(player, rating);
  const sealed = revealState === 'sealed' || revealState === 'tear';

  const face = (
    <CardSvg
      size={size}
      uid={uid}
      title={title}
      name={plateName(player)}
      jersey={jerseyText(player.jersey)}
      shownOvr={shownOvr}
      tier={tier}
      tierTag={tierLabel(tier)}
      stateTag={provisional ? copy.provisionalTag : rusty ? copy.rustyTag : null}
      stateTagShort={provisional ? copy.provisionalTagShort : rusty ? copy.rustyTag : null}
      stateFill={rusty ? '#ffc299' : '#ffffa1'}
      ribbon={copy.ribbon[preset ?? art?.preset ?? 'rookie_card']}
      subline={subline(rating, player.id, Date.now())}
      attrs={showAttrs}
      preset={preset ?? art?.preset ?? 'rookie_card'}
      figure={figure}
      nudge={art?.nudge ?? null}
      flags={flags}
      shadowColor={SCREEN_SHADOW}
      animate={revealState !== undefined}
      singleDigitTitle={copy.singleDigits}
    />
  );

  const body =
    revealState === undefined ? (
      face
    ) : (
      <span className="tts-cards-flip" data-face={sealed ? 'back' : 'front'}>
        <span className="tts-cards-flip-inner">
          <span className="tts-cards-face" aria-hidden={sealed}>
            {face}
          </span>
          <span className="tts-cards-face tts-cards-face-back" aria-hidden={!sealed}>
            <CardBackSvg
              uid={uid}
              shadowColor={SCREEN_SHADOW}
              tearing={revealState === 'tear'}
              label={copy.label.sealed}
            />
          </span>
        </span>
      </span>
    );

  const common = {
    'data-testid': 'player-card',
    'data-size': size,
    'data-tier': tier ?? 'unrated',
    'data-state': state,
    'data-reveal': revealState,
    'data-ovr': ovr ?? '??',
    className: clsx('relative block shrink-0', WIDTH[size], className),
  } as const;

  if (onPress) {
    return (
      <button
        type="button"
        onClick={onPress}
        aria-label={sealed ? copy.label.sealed : title}
        {...common}
        className={clsx(common.className, 'tts-press cursor-pointer text-left')}
        style={{ minHeight: 44, minWidth: 44 }}
      >
        {body}
      </button>
    );
  }
  return <div {...common}>{body}</div>;
}
